import { useCallback, useEffect, useState } from 'react';
import { Group, Icon, Skeleton, StatusPill } from '@store/ui';
import { ApiError, formatMoney, type OrderSummary, type ProductSummary } from '../api';

/**
 * Small data-loading hook: enough for this app, no dependency needed.
 *
 * Two ways to ask again, and what separates them is what the screen is allowed
 * to claim while it waits.
 *
 * `reload()` is a first load, repeated: it raises `loading` and clears the
 * error, which is what a fresh mount and a «Повторить» button both want, and it
 * is the behaviour every screen already had.
 *
 * `refresh()` is a re-read of something the customer is already looking at. It
 * touches nothing until the answer arrives — same `data`, same `error`, still
 * `loading: false`. `OrderScreen` re-reads an unpaid order every three seconds
 * and draws a skeleton whenever `loading`, so with only the first path the
 * rendered order was thrown away and repainted as a skeleton for one frame on
 * every tick. That is the flash: a screen announcing it has nothing, twenty
 * times a minute, about data it is still holding.
 */
export function useAsync<T>(loader: () => Promise<T>, deps: unknown[]) {
  const [state, setState] = useState<{
    data: T | null;
    error: string | null;
    loading: boolean;
  }>({ data: null, error: null, loading: true });

  const run = useCallback((background: boolean) => {
    let cancelled = false;
    if (!background) setState((previous) => ({ ...previous, loading: true, error: null }));

    loader()
      .then((data) => {
        if (!cancelled) setState({ data, error: null, loading: false });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const message = error instanceof ApiError ? error.message : 'Не удалось загрузить данные';
        /* A background re-read that fails keeps what is on screen. Nobody asked
           for it, the screen is not empty, and replacing a rendered order with
           «не удалось загрузить» over one dropped request is the same flash
           wearing a different hat — the next tick would put the order back. */
        setState((previous) =>
          background && previous.data ? previous : { data: null, error: message, loading: false },
        );
      });

    return () => {
      cancelled = true;
    };
  }, deps);

  const reload = useCallback(() => run(false), [run]);
  const refresh = useCallback(() => run(true), [run]);

  useEffect(() => run(false), [run]);

  return { ...state, reload, refresh };
}

/**
 * Room for a state that is the only thing on its screen.
 *
 * `.state` is a small centred block, which is right when it sits among other
 * content. When it is the whole screen it lands just under the heading with two
 * hundred pixels of nothing beneath it, and that dead band reads as a screen
 * that failed to finish rendering rather than as one that is genuinely empty.
 */
export const fillScreen = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  minHeight: '46vh',
} as const;

/** What a catalogue row is allowed to say about stock, and nothing else. */
export type StockBadge = { kind: 'soldOut' } | { kind: 'left'; count: number } | null;

/**
 * The stock badge, decided once and away from the JSX so it can be called.
 *
 * `available` carries two different facts in one field and the API is explicit
 * about which is which — `catalog.ts` sends
 * `row.tracksInventory ? (stock.get(row.id) ?? 0) : null`. `null` means this
 * product does not count units at all; a number is a real count of things that
 * exist. Those are two different silences and the row was treating them as one.
 *
 * The threshold is gone, and that is the decision here. `available <= 3` hid
 * the count on anything better stocked, so a product with 24 in stock and a
 * product that never counts anything rendered identically: nothing. That blank
 * row could not be read — «их много» and «мы не считаем» are different answers
 * to the question the customer is asking, and a badge that only ever appears
 * below four makes them guess which one they are looking at. Stating the count
 * whenever there is one leaves silence meaning exactly one thing: this product
 * does not track stock. Scarcity is then read off the number, which is what a
 * number is for — the row does not get to decide on the customer's behalf that
 * 24 is not worth knowing. It costs a pill on rows that used to be quiet; that
 * is the price of the blank row meaning something.
 *
 * Everything that is not a count of whole things gets nothing, because the one
 * thing this badge may never do is invent a number. A fraction is floored
 * rather than shown — «Осталось 2,5» is not a quantity anybody can buy, and
 * flooring can only understate. Zero and below is sold out: a negative is an
 * oversold row, and the honest thing to say about it is that there is none
 * left, not «Осталось -2», and not silence, which would leave the row looking
 * like one that never counted at all.
 */
export function stockBadge(available: number | null | undefined): StockBadge {
  // Covers `null`, and an older API that omits the field entirely.
  if (typeof available !== 'number' || !Number.isFinite(available)) return null;

  const count = Math.floor(available);

  return count <= 0 ? { kind: 'soldOut' } : { kind: 'left', count };
}

/**
 * One product, as a line rather than a card.
 *
 * A card per product made six products fill three screens and gave every item
 * the same visual weight. A row lets the title carry the ranking and puts six
 * of them in view at once, which is what a catalogue is for.
 */
export function ProductRow({
  product,
  onOpen,
}: {
  product: ProductSummary;
  onOpen: (shortId: string) => void;
}) {
  const badge = stockBadge(product.available);

  return (
    <button type="button" className="product" onClick={() => onOpen(product.shortId)}>
      <span className="product__body">
        <span className="product__title">{product.title}</span>
        {product.subtitle ? (
          <span className="product__subtitle clamp-2">{product.subtitle}</span>
        ) : null}
        <span className="product__meta">
          <span className="price">{formatMoney(product.price)}</span>
          {product.listPrice ? (
            <span className="price__old">{formatMoney(product.listPrice)}</span>
          ) : null}
          {/* One badge or none, and `stockBadge` is the only thing that
              chooses. The count rendered here is the one it returned — never
              `product.available` again, or the floor and the guards above stop
              applying at the only place they matter. */}
          {badge?.kind === 'soldOut' ? <StatusPill tone="danger">Нет в наличии</StatusPill> : null}
          {badge?.kind === 'left' ? (
            <StatusPill tone="warning">Осталось {badge.count}</StatusPill>
          ) : null}
        </span>
      </span>
      <span className="rowitem__chevron" style={{ alignSelf: 'center' }}>
        <Icon name="chevron" size={17} />
      </span>
    </button>
  );
}

export function ListSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <Group>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="product">
          <span className="product__body">
            <Skeleton height={17} width="62%" />
            <div style={{ height: 6 }} />
            <Skeleton height={14} width="44%" />
            <div style={{ height: 10 }} />
            <Skeleton height={17} width="28%" />
          </span>
        </div>
      ))}
    </Group>
  );
}

/**
 * Order status, as a glyph plus a word.
 *
 * Three states an operator cares about and everything else stays neutral —
 * colouring every status turns the list into bunting and stops any one of them
 * meaning anything.
 */
export function OrderStatus({ status, label }: { status: string; label: string }) {
  if (status === 'delivered') {
    return (
      <StatusPill tone="success" icon="circleCheck">
        {label}
      </StatusPill>
    );
  }
  if (status === 'manual_review') {
    return (
      <StatusPill tone="warning" icon="clock">
        {label}
      </StatusPill>
    );
  }
  if (status === 'cancelled' || status === 'expired' || status === 'refunded') {
    return <StatusPill>{label}</StatusPill>;
  }
  /* Waiting is the ordinary state, so it stays neutral. It used to be tinted
     with the accent, which put the app's one blue on a list that is otherwise
     monochrome — and on the most common status of all, so every list had it. */
  return <StatusPill icon="clock">{label}</StatusPill>;
}

/**
 * One order in the list.
 *
 * Not a `Row`: an order has a price *and* a status *and* a reference, and the
 * generic row hands all three to a single trailing slot that then eats the
 * title. Here each line is its own pair, so the name can shorten without the
 * price or the status being allowed to.
 */
export function OrderRow({ order, onOpen }: { order: OrderSummary; onOpen: () => void }) {
  return (
    <button type="button" className="orderrow" onClick={onOpen}>
      <span className="orderrow__body">
        <span className="orderrow__line">
          <span className="orderrow__title">{order.title ?? order.orderNumber}</span>
          <span className="price">{formatMoney(order.totalAmount)}</span>
        </span>
        <span className="orderrow__line">
          <span className="orderrow__meta">
            {order.orderNumber} · {new Date(order.createdAt).toLocaleDateString('ru-RU')}
          </span>
          <OrderStatus status={order.status} label={order.statusLabel} />
        </span>
      </span>
      <span className="rowitem__chevron">
        <Icon name="chevron" size={17} />
      </span>
    </button>
  );
}
